import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
} from 'react';
import { Select } from 'antd';
import { Lock, Plus } from 'lucide-react';
import { BrackedAddIcon, PlusCircleIcon } from '@/assets/icons/icons';
import useToken from '@/common/helper/useToken';
import {
  GetCustomerCredentials,
  type CredentialData,
} from '@/apis/customerCredentials';
import {
  WebhookFailureAction,
  WebhookNotifyChannel,
  type CallWebhookActionData,
  type TextTemplateChunk,
  type WebhookHeader,
} from '@/proto/logiblocks/logiblocks';
import CreateCredentialDrawer from '@/pages/Settings/Credentials/components/CreateCredentialDrawer';
import styles from '../NodeCustomization.module.css';
import webhookStyles from './CallWebhookActionCustomization.module.css';
import { CustomizationComponent } from '@/pages/Logiblocks/types';
import { TemplatedTextArea } from '@/pages/Logiblocks/variables/TemplatedTextArea/TemplatedTextArea';
import {
  chunksEqual,
  getPlainTemplateText,
  transformTemplateText,
} from '@/pages/Logiblocks/variables/TemplatedTextArea/templateSerialization';
import { ALL_VARIABLE_TYPES } from '@/pages/Logiblocks/variables/variableRefs';
import { FieldErrorLabel } from '../shared';
import { CallWebhookActionFields } from './CallWebhookActionNodeInfo';
import {
  AUTH_TYPE_FILTER_OPTIONS,
  CONTENT_TYPE_HEADER,
  DEFAULT_CALL_WEBHOOK_ACTION,
  DEFAULT_CONTENT_TYPE,
  METHOD_OPTIONS,
  METHODS_WITHOUT_BODY,
  NOTIFY_CHANNEL_OPTIONS,
  ON_FAILURE_OPTIONS,
  RETRY_POLICY_PRESETS,
  type AuthTypeFilter,
} from './defaults';
import {
  hasTemplateContent,
  type ValidationError,
} from '@/pages/Logiblocks/fieldValidation';

/**
 * The first header name used twice, or undefined. Case-insensitive, because
 * HTTP header names are, and the injected credential header participates: a
 * custom row must not shadow it.
 *
 * `lockedHeaderName` is the header a selected credential injects at request
 * time, or null if it doesn't inject one (API_KEY with QUERY placement puts
 * its value on the URL instead). Mirrors apply_auth() in
 * customer_credentials/services/operations.py: OAuth always injects
 * "Authorization"; an API_KEY's header name is parsed out of `details`, the
 * only place the param name reaches the frontend (see
 * CredentialListItemSerializer.get_details on the backend).
 */
export function findDuplicateHeaderName(
  headers: WebhookHeader[],
  lockedHeaderName: string | null
): string | undefined {
  const seen = new Set<string>();
  if (lockedHeaderName) seen.add(lockedHeaderName.toLowerCase());
  for (const header of headers) {
    const name = header.key.trim().toLowerCase();
    if (!name) continue;
    if (seen.has(name)) return header.key.trim();
    seen.add(name);
  }
  return undefined;
}

/**
 * Returns an error if a JSON body doesn't parse with each variable and mention
 * replaced by `0`. The check is on the document's shape, since a variable's
 * value is known only at run time.
 */
export function jsonBodyError(
  bodyChunks: TextTemplateChunk[]
): ValidationError | undefined {
  let withPlaceholders = '';
  for (const chunk of bodyChunks) {
    if (chunk.text !== undefined) {
      withPlaceholders += chunk.text;
    } else {
      withPlaceholders += '0';
    }
  }
  try {
    JSON.parse(withPlaceholders);
    return undefined;
  } catch {
    return {
      type: 'SYNTAX_ERROR',
      field: CallWebhookActionFields.BODY,
      message: 'Body must be valid JSON',
    };
  }
}

/**
 * Returns a header's value as plain text: `value_chunks` joined if every chunk
 * is text. A value holding a variable or a mention returns `''`, since its text
 * is known only at run time.
 */
function getHeaderValueText(header: WebhookHeader): string {
  return getPlainTemplateText(header.value_chunks) ?? '';
}

/**
 * Returns the content type a webhook sends: its Content-Type header row's text,
 * else its `content_type` field, else `DEFAULT_CONTENT_TYPE`.
 */
function getEffectiveContentType(webhook: CallWebhookActionData): string {
  const contentTypeHeader = webhook.headers.find(
    (h) => h.key.trim().toLowerCase() === CONTENT_TYPE_HEADER.toLowerCase()
  );
  const headerContentType = contentTypeHeader
    ? getHeaderValueText(contentTypeHeader).trim()
    : '';
  return headerContentType || webhook.content_type || DEFAULT_CONTENT_TYPE;
}

/**
 * Whether a webhook's effective Content-Type marks its body as JSON. The
 * header row is the source of truth when present, matching how `emit()`
 * derives the proto's own content_type field.
 */
export function isJsonContentType(webhook: CallWebhookActionData): boolean {
  const effectiveContentType = getEffectiveContentType(webhook);
  return (
    effectiveContentType.split(';')[0].trim().toLowerCase() ===
      DEFAULT_CONTENT_TYPE || effectiveContentType.includes('+json')
  );
}

/**
 * Ensures a Content-Type header row is present, synthesizing one from the
 * proto's own content_type field when it's missing.
 *
 * The header row is normally the source of truth -- emit() keeps
 * content_type mirrored from it on every edit -- but that sync only ever
 * runs forward from an edit made in this panel. Data built another way (a
 * test harness, an older editor, the workflow agent) can set content_type
 * directly with no matching row, and without this, that content type would
 * be invisible in the Headers list even though it's genuinely being sent.
 */
export function withContentTypeHeader(
  webhook: CallWebhookActionData
): CallWebhookActionData {
  const hasContentTypeHeader = webhook.headers.some(
    (h) => h.key.trim().toLowerCase() === CONTENT_TYPE_HEADER.toLowerCase()
  );
  if (hasContentTypeHeader) return webhook;

  return {
    ...webhook,
    headers: [
      {
        key: CONTENT_TYPE_HEADER,
        value_chunks: [{ text: webhook.content_type || DEFAULT_CONTENT_TYPE }],
      },
      ...webhook.headers,
    ],
  };
}

/**
 * Returns a JSON body's chunks with the document indented, each variable and
 * mention kept in its place. Returns the input unchanged if the body doesn't
 * parse with each variable and mention as string content, as a variable used
 * as a bare value doesn't.
 */
export function prettyPrintJsonBody(
  bodyChunks: TextTemplateChunk[]
): TextTemplateChunk[] {
  let formattedChunks: TextTemplateChunk[] | null = null;
  try {
    formattedChunks = transformTemplateText(bodyChunks, (markedText) =>
      JSON.stringify(JSON.parse(markedText), null, 2)
    );
  } catch {
    return bodyChunks;
  }
  return formattedChunks ?? bodyChunks;
}

/** The delete affordance from Figma: 20x16, filled with the text colour. */
function DeleteIcon() {
  return (
    <svg
      width='20'
      height='16'
      viewBox='0 0 20 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      aria-hidden='true'
    >
      <path
        d='M5.99967 12.6667C5.99967 13.4 6.59967 14 7.33301 14H12.6663C13.3997 14 13.9997 13.4 13.9997 12.6667V4.66667H5.99967V12.6667ZM14.6663 2.66667H12.333L11.6663 2H8.33301L7.66634 2.66667H5.33301V4H14.6663V2.66667Z'
        fill='currentColor'
      />
    </svg>
  );
}

/**
 * Stops a press on the "Insert variable" button from taking focus off the
 * field. The button inserts at the caret, and a blur first would reformat the
 * body and move the caret before the variable is inserted.
 */
function keepEditorFocus(event: MouseEvent<HTMLButtonElement>) {
  event.preventDefault();
}

function credentialAuthHeaderName(credential: CredentialData): string | null {
  if (credential.auth_type === 'OAUTH_CLIENT_CREDENTIALS') {
    return 'Authorization';
  }
  const [placement, paramName] = credential.details.split(': ');
  return placement === 'Header' && paramName ? paramName : null;
}

/**
 * Customization UI for CALL_WEBHOOK_ACTION nodes.
 *
 * Follows the raw-antd + NodeCustomization.module.css convention used by its
 * suspend-based siblings (Slack, Delay) rather than CustomInput/CustomSelect,
 * since those default to hardcoded light-mode colors and would need explicit
 * textColor/bgColor overrides to honor dark mode -- this pattern gets dark
 * mode for free via the module's CSS variable references.
 *
 * The URL, header and body fields pass variableTriggerChar="{{", so a bare "{"
 * in JSON body syntax doesn't open the variable picker.
 */
export const CallWebhookActionCustomization: CustomizationComponent = ({
  nodeId,
  nodeData,
  onChange,
  isReadOnly = false,
  validationErrors,
}) => {
  const urlError = validationErrors?.find(
    (e) => e.field === CallWebhookActionFields.URL
  );
  const bodyError = validationErrors?.find(
    (e) => e.field === CallWebhookActionFields.BODY
  );
  const headersError = validationErrors?.find(
    (e) => e.field === CallWebhookActionFields.HEADERS
  );

  const { getToken } = useToken();
  const [credentials, setCredentials] = useState<CredentialData[]>([]);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  // Whether this user may add credentials. Only an explicit `false` from the
  // server hides the way to Settings; anything else (an older response, a
  // failed fetch) leaves the usual hint.
  const [canManageCredentials, setCanManageCredentials] = useState(true);
  // True only once the list has actually loaded, so a failed fetch is never
  // mistaken for "the credential was deleted".
  const [credentialsLoaded, setCredentialsLoaded] = useState(false);
  // The credential dropdown, controlled so its "New credentials" footer can
  // close it before the drawer opens, and whether that drawer is open.
  const [credentialDropdownOpen, setCredentialDropdownOpen] = useState(false);
  const [newCredentialOpen, setNewCredentialOpen] = useState(false);

  const [webhook, setWebhook] = useState<CallWebhookActionData>(
    nodeData.call_webhook_action ?? DEFAULT_CALL_WEBHOOK_ACTION
  );
  // Explicit user choice of auth-type filter. Undefined means "not yet
  // touched" -- derived below from the currently selected credential instead.
  const [authTypeOverride, setAuthTypeOverride] = useState<
    AuthTypeFilter | undefined
  >(undefined);
  // Whether the body field has focus. `displayBodyChunks` is formatted only if
  // it doesn't, so formatting never changes the text while the author types.
  const [bodyFocused, setBodyFocused] = useState(false);

  // Fetch credentials on mount -- no Redux slice for this yet, and the list
  // is small/cheap enough that a plain component-local fetch is lighter
  // weight than adding one.
  const loadCredentials = useCallback(() => {
    const token = getToken();
    if (!token) return;
    setCredentialsLoading(true);
    GetCustomerCredentials(token)
      .then((res) => {
        const data = res?.data?.data;
        setCredentials(data?.is_success ? (data.data ?? []) : []);
        setCredentialsLoaded(Boolean(data?.is_success));
        setCanManageCredentials(
          (data as { can_manage?: boolean } | undefined)?.can_manage !== false
        );
      })
      .catch(() => {
        setCredentials([]);
        setCredentialsLoaded(false);
      })
      .finally(() => setCredentialsLoading(false));
  }, [getToken]);

  useEffect(() => {
    loadCredentials();
  }, [loadCredentials]);

  // Update local state when nodeData changes (e.g., node switching).
  //
  // Deliberately *not* where the JSON body gets pretty-printed: nodeData can
  // get a new object reference on renders that have nothing to do with this
  // node (LBCanvas re-deriving its nodes array), and an effect keyed on it
  // would re-fire then too -- silently overwriting a pretty-printed-on-load
  // body back to whatever's still in nodeData (raw, since pretty-printing on
  // load is local-only and never emitted). Deriving the formatted body from the
  // stored body on every render (`displayBodyChunks`) has no such race.
  //
  // withContentTypeHeader doesn't have the same race: unlike body
  // pretty-printing, it derives strictly from nodeData's own fields (never
  // from something typed locally that hasn't been emitted yet), so
  // re-deriving it on every incidental re-fire is idempotent -- and once the
  // row exists, headers are emitted on every keystroke, so nodeData already
  // carries it on any later re-render regardless.
  useEffect(() => {
    setWebhook(
      withContentTypeHeader(
        nodeData.call_webhook_action ?? DEFAULT_CALL_WEBHOOK_ACTION
      )
    );
    setAuthTypeOverride(undefined);
  }, [nodeData]);

  const urlPickerTriggerRef = useRef<(() => void) | null>(null);
  const bodyPickerTriggerRef = useRef<(() => void) | null>(null);

  const emit = (next: CallWebhookActionData) => {
    // The Content-Type header row is what the builder edits; the proto's
    // content_type is derived from it on every change so the two can never
    // disagree about what will be sent.
    const synced: CallWebhookActionData = {
      ...next,
      content_type: getEffectiveContentType(next),
    };
    setWebhook(synced);
    onChange({ call_webhook_action: synced });
  };

  const isJsonBody = isJsonContentType(webhook);
  const methodHasNoBody = METHODS_WITHOUT_BODY.includes(webhook.method);

  const bodyHasContent = hasTemplateContent(webhook.body_chunks);

  const localBodyError: ValidationError | undefined =
    !methodHasNoBody && isJsonBody && bodyHasContent
      ? jsonBodyError(webhook.body_chunks)
      : undefined;

  // Recomputed every render from the stored body rather than held in state,
  // because the nodeData effect would reset that state to the unformatted body.
  // The body is formatted only if the field doesn't have focus, so a keystroke
  // that completes valid JSON (e.g. a closing brace) can't move the editor's
  // text under the caret.
  let displayBodyChunks = webhook.body_chunks;
  if (!bodyFocused && isJsonBody && bodyHasContent) {
    displayBodyChunks = prettyPrintJsonBody(webhook.body_chunks);
  }

  const selectedCredential = credentials.find(
    (c) => c.id === webhook.credential_id
  );
  // The node still names a credential that no longer exists (it was deleted in
  // Settings, which is allowed while automations use it). Runs will fail, so
  // say so here and let the author pick another.
  const credentialMissing =
    Boolean(webhook.credential_id) && credentialsLoaded && !selectedCredential;
  const authTypeFilter: AuthTypeFilter =
    authTypeOverride ?? selectedCredential?.auth_type ?? 'NONE';
  const visibleCredentials =
    authTypeFilter === 'NONE'
      ? credentialMissing
        ? credentials
        : []
      : credentials.filter((c) => c.auth_type === authTypeFilter);

  const canAddCredential = canManageCredentials && !isReadOnly;

  const openNewCredential = () => {
    setCredentialDropdownOpen(false);
    setNewCredentialOpen(true);
  };

  // The drawer saved a credential: close it and select the new one, so the
  // author carries on where they were. The type filter follows it, since the
  // dropdown only lists credentials of the chosen type.
  const handleCredentialCreated = (created: CredentialData | undefined) => {
    setNewCredentialOpen(false);
    if (!created) {
      loadCredentials();
      return;
    }
    setCredentials((current) => [
      ...current.filter((c) => c.id !== created.id),
      created,
    ]);
    setCredentialsLoaded(true);
    setAuthTypeOverride(created.auth_type);
    emit({ ...webhook, credential_id: created.id });
  };

  const handleAuthTypeFilterChange = (value: AuthTypeFilter) => {
    setAuthTypeOverride(value);
    if (selectedCredential && selectedCredential.auth_type !== value) {
      emit({ ...webhook, credential_id: '' });
    }
  };

  const lockedHeaderName = selectedCredential
    ? credentialAuthHeaderName(selectedCredential)
    : null;

  // Validation the panel can do itself, so a bad config is caught before a
  // run rather than at one. The injected credential header counts, since a
  // custom row cannot reuse its name.
  const duplicateHeaderError = findDuplicateHeaderName(
    webhook.headers,
    lockedHeaderName
  );

  const updateHeader = (index: number, patch: Partial<WebhookHeader>) => {
    const headers = webhook.headers.map((header, i) =>
      i === index ? { ...header, ...patch } : header
    );
    emit({ ...webhook, headers });
  };

  const addHeader = () => {
    emit({
      ...webhook,
      headers: [...webhook.headers, { key: '', value_chunks: [] }],
    });
  };

  const removeHeader = (index: number) => {
    emit({
      ...webhook,
      headers: webhook.headers.filter((_, i) => i !== index),
    });
  };

  // Saves the formatted body, so what's saved matches what's shown.
  // `displayBodyChunks` changes only what the body field shows.
  const persistPrettyBody = () => {
    if (!isJsonBody) return;
    const formatted = prettyPrintJsonBody(webhook.body_chunks);
    if (chunksEqual(formatted, webhook.body_chunks)) {
      return;
    }
    emit({ ...webhook, body_chunks: formatted });
  };

  const selectedRetryIndex = RETRY_POLICY_PRESETS.findIndex(
    (preset) =>
      preset.value.max_attempts === (webhook.retry_policy?.max_attempts ?? 1)
  );

  return (
    <>
      <div className={styles.customizationSection}>
        <div className={webhookStyles.sectionHeader}>
          <label className={webhookStyles.sectionHeaderLabel}>Request</label>
          {!isReadOnly && (
            <button
              type='button'
              className={webhookStyles.insertVariableButton}
              onMouseDown={keepEditorFocus}
              onClick={() => urlPickerTriggerRef.current?.()}
              aria-label='Insert variable'
            >
              <BrackedAddIcon />
            </button>
          )}
        </div>
        <div className={webhookStyles.twoColumnRow}>
          <Select
            value={webhook.method || 'POST'}
            onChange={(value) => emit({ ...webhook, method: value })}
            disabled={isReadOnly}
            style={{ width: '100%' }}
            className={`agentSelect agentSelect--darkText ${styles.formSelect}`}
          >
            {METHOD_OPTIONS.map((method) => (
              <Select.Option key={method} value={method}>
                {method}
              </Select.Option>
            ))}
          </Select>
          <div className={webhookStyles.shrinkableCell}>
            <TemplatedTextArea
              nodeId={nodeId}
              chunks={webhook.url_chunks}
              onChange={(chunks) => emit({ ...webhook, url_chunks: chunks })}
              placeholder='https://example.com/webhook'
              multiline={false}
              compact
              disabled={isReadOnly}
              variableTriggerChar='{{'
              chipTrailingSpace={false}
              typeFilter={ALL_VARIABLE_TYPES}
              onTriggerPickerRef={(fn) => {
                urlPickerTriggerRef.current = fn;
              }}
            />
          </div>
        </div>
        <span className={webhookStyles.helperText}>
          HTTPS only. Variables are filled at run time.
        </span>
        <FieldErrorLabel error={urlError} />
      </div>

      <div className={styles.customizationSection}>
        <label className={styles.sectionLabel}>Authentication type</label>
        <Select
          value={authTypeFilter}
          onChange={handleAuthTypeFilterChange}
          disabled={isReadOnly}
          style={{ width: '100%' }}
          className={`agentSelect agentSelect--darkText ${styles.formSelect}`}
        >
          {AUTH_TYPE_FILTER_OPTIONS.map((opt) => (
            <Select.Option key={opt.value} value={opt.value}>
              {opt.label}
            </Select.Option>
          ))}
        </Select>
      </div>

      {(authTypeFilter !== 'NONE' || credentialMissing) && (
        <div className={styles.customizationSection}>
          <label className={styles.sectionLabel}>Credential</label>
          <Select
            value={credentialMissing ? '' : webhook.credential_id || ''}
            onChange={(value) => emit({ ...webhook, credential_id: value })}
            disabled={isReadOnly || credentialsLoading}
            loading={credentialsLoading}
            open={credentialDropdownOpen}
            onOpenChange={setCredentialDropdownOpen}
            popupRender={(menu) => (
              <>
                {menu}
                {canAddCredential && (
                  <div className={webhookStyles.newCredentialFooter}>
                    <button
                      type='button'
                      className={webhookStyles.newCredentialButton}
                      // Keep the Select from losing focus (and closing) before
                      // the click lands.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={openNewCredential}
                    >
                      <PlusCircleIcon />
                      New credentials
                    </button>
                  </div>
                )}
              </>
            )}
            style={{ width: '100%' }}
            className={`agentSelect agentSelect--darkText ${styles.formSelect}`}
          >
            <Select.Option value=''>None</Select.Option>
            {visibleCredentials.map((credential) => (
              <Select.Option key={credential.id} value={credential.id}>
                {credential.name}
              </Select.Option>
            ))}
          </Select>
          {credentialMissing && (
            <div
              className={`ant-form-item-explain-error ${webhookStyles.credentialMissing}`}
            >
              Credential missing. Select a credential.
            </div>
          )}
          <span className={webhookStyles.helperText}>
            {canManageCredentials
              ? 'Saved connections keep credentials at the workspace level. Manage in Settings.'
              : 'Ask a workspace admin to add credentials.'}
          </span>
        </div>
      )}

      <div className={styles.customizationSection}>
        <label className={styles.sectionLabel}>Headers</label>
        <div className={webhookStyles.headerList}>
          {lockedHeaderName && selectedCredential && (
            <div className={webhookStyles.headerRow}>
              <div className={webhookStyles.lockedHeaderName}>
                <Lock size={14} style={{ flexShrink: 0 }} />
                <span className={webhookStyles.lockedHeaderNameText}>
                  {lockedHeaderName}
                </span>
              </div>
              <input
                type='text'
                value={`***from ${selectedCredential.name}***`}
                disabled
                readOnly
                className={webhookStyles.lockedHeaderValue}
              />
            </div>
          )}
          {webhook.headers.map((header, index) => (
            <div
              // eslint-disable-next-line react/no-array-index-key -- headers have no ids; each row's inputs are controlled, and TemplatedTextArea resyncs when its chunks change
              key={index}
              className={
                isReadOnly
                  ? webhookStyles.twoColumnRow
                  : webhookStyles.headerRow
              }
            >
              <input
                type='text'
                value={header.key}
                placeholder='Header name'
                onChange={(e) => updateHeader(index, { key: e.target.value })}
                className={webhookStyles.headerNameInput}
                disabled={isReadOnly}
              />
              <div className={webhookStyles.shrinkableCell}>
                <TemplatedTextArea
                  nodeId={nodeId}
                  chunks={header.value_chunks}
                  onChange={(chunks) =>
                    updateHeader(index, { value_chunks: chunks })
                  }
                  placeholder='Header value'
                  multiline={false}
                  compact
                  disabled={isReadOnly}
                  variableTriggerChar='{{'
                  chipTrailingSpace={false}
                  typeFilter={ALL_VARIABLE_TYPES}
                />
              </div>
              {!isReadOnly && (
                <button
                  type='button'
                  onClick={() => removeHeader(index)}
                  aria-label='Remove header'
                  className={webhookStyles.removeHeaderButton}
                >
                  <DeleteIcon />
                </button>
              )}
            </div>
          ))}
        </div>
        {!isReadOnly && (
          <button
            type='button'
            onClick={addHeader}
            className={webhookStyles.addHeaderButton}
          >
            <Plus size={14} /> Add header
          </button>
        )}
        <FieldErrorLabel
          error={
            headersError ??
            (duplicateHeaderError
              ? {
                  type: 'VALUE_ERROR',
                  field: CallWebhookActionFields.HEADERS,
                  message: 'Header names must be unique',
                }
              : undefined)
          }
        />
      </div>

      {!methodHasNoBody && (
        <div className={styles.customizationSection}>
          <div className={webhookStyles.sectionHeader}>
            <label className={webhookStyles.sectionHeaderLabel}>Body</label>
            {!isReadOnly && (
              <button
                type='button'
                className={webhookStyles.insertVariableButton}
                onMouseDown={keepEditorFocus}
                onClick={() => bodyPickerTriggerRef.current?.()}
                aria-label='Insert variable'
              >
                <BrackedAddIcon />
              </button>
            )}
          </div>
          <TemplatedTextArea
            nodeId={nodeId}
            chunks={displayBodyChunks}
            onChange={(chunks) => emit({ ...webhook, body_chunks: chunks })}
            placeholder='{ "key": "{{node.variable}}" }'
            multiline
            compact
            monospace
            disabled={isReadOnly}
            variableTriggerChar='{{'
            chipTrailingSpace={false}
            typeFilter={ALL_VARIABLE_TYPES}
            onTriggerPickerRef={(fn) => {
              bodyPickerTriggerRef.current = fn;
            }}
            onFocus={() => {
              setBodyFocused(true);
              // The at-rest view is the pretty form. Adopting it as the
              // template now keeps focusing from swapping that text for the
              // raw one under the caret, which would move the caret.
              persistPrettyBody();
            }}
            onBlur={() => {
              setBodyFocused(false);
              persistPrettyBody();
            }}
          />
          <FieldErrorLabel error={bodyError ?? localBodyError} />
        </div>
      )}

      <div className={styles.customizationSection}>
        <label className={styles.sectionLabel}>On failure</label>
        <div className={webhookStyles.twoColumnRow}>
          <Select
            value={selectedRetryIndex === -1 ? 0 : selectedRetryIndex}
            onChange={(index) =>
              emit({
                ...webhook,
                retry_policy: RETRY_POLICY_PRESETS[index].value,
              })
            }
            disabled={isReadOnly}
            style={{ width: '100%' }}
            className={`agentSelect agentSelect--darkText ${styles.formSelect}`}
          >
            {RETRY_POLICY_PRESETS.map((preset, index) => (
              <Select.Option key={preset.label} value={index}>
                {preset.label}
              </Select.Option>
            ))}
          </Select>
          <Select
            value={
              webhook.on_failure ??
              WebhookFailureAction.WEBHOOK_FAILURE_CONTINUE
            }
            onChange={(value) => emit({ ...webhook, on_failure: value })}
            disabled={isReadOnly}
            style={{ width: '100%' }}
            className={`agentSelect agentSelect--darkText ${styles.formSelect}`}
          >
            {ON_FAILURE_OPTIONS.map((opt) => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        </div>
        {webhook.on_failure === WebhookFailureAction.WEBHOOK_FAILURE_NOTIFY && (
          <div className={webhookStyles.notifyChannelRow}>
            <Select
              value={
                webhook.notify_channel ??
                WebhookNotifyChannel.WEBHOOK_NOTIFY_IN_APP
              }
              onChange={(value) => emit({ ...webhook, notify_channel: value })}
              disabled={isReadOnly}
              style={{ width: '100%' }}
              className={`agentSelect agentSelect--darkText ${styles.formSelect}`}
            >
              {NOTIFY_CHANNEL_OPTIONS.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </div>
        )}
        <span className={webhookStyles.helperText}>
          Every attempt is recorded in the Runs section.
        </span>
      </div>

      {/* The Settings > Credentials drawer, opened over this panel so the
          author never leaves the automation. Mounted only while open. */}
      {newCredentialOpen && (
        <CreateCredentialDrawer
          open
          onClose={() => setNewCredentialOpen(false)}
          onCreated={handleCredentialCreated}
          defaultAuthType={
            authTypeFilter === 'NONE' ? undefined : authTypeFilter
          }
        />
      )}
    </>
  );
};
